import React from "react";

import {StandardLayout} from "../../../../components/StandardLayout";
import {CategoryTree} from "../CategoryTree";
import {PostList} from "../../../../components/PostList";
import {LoadingView} from "../../../../designSystem/components/LoadingView";
import {ActionButton} from "../../../../designSystem/components/ActionButton";
import {Dialog} from "../../../../designSystem/components/Dialog";
import {Typography} from "../../../../designSystem/components/Typography";
import {CONTENT_MANAGEMENT_MODAL_CONTENT, ContentManagementModalContent} from "../ContentManagementModalContent";

import {
  useContentListView,
  useContentManagementData,
  useContentManagementMutations,
  useContentManagementState,
  useExportContentCategories
} from "../../hooks";

export const ContentManagementFeature = ({ userToken }) => {
  const {categories, posts, isLoading, refresh} = useContentManagementData({userToken});
  const {
    selectedCategory,
    dialogContent,
    editingPost,
    setSelectedCategory,
    setDialogContent,
    openEdit,
    openCreatePost,
    handleCloseDialog,
    handleSelectCategory,
    handleRenameCategory,
    handleAddCategory,
    handleDeleteCategoryDialog,
    handleMoveCategoryDialog,
    handleChangeCoverDialog,
    handleDeletePostDialog,
  } = useContentManagementState();
  const {
    onSaveRenameCategory,
    onSaveChangeCoverImage,
    onAddCategory,
    onMoveCategory,
    onDeleteCategory,
    createPost,
    onConfirmDeletePost,
  } = useContentManagementMutations({
    userToken,
    selectedCategory,
    editingPost,
    refresh,
    setSelectedCategory,
    setDialogContent,
  });
  const {filteredPosts} = useContentListView({posts, selectedCategory});

  const { exportData } = useExportContentCategories({ posts, categories })

  // Rendering
  if (isLoading || !userToken)
    return <LoadingView/>

  return (
    <StandardLayout
      headerActionLabel="新增文章"
      headerActionOnClick={openCreatePost}
      headerTitle="衛教資訊管理"
      body={
        <div className="text-md text-center">
          <div className="grid grid-cols-[320px_1fr] gap-4">
            {/* Left */}
            <aside className="rounded-xl p-3 shadow bg-white h-[calc(100vh-120px)] overflow-y-scroll">
              <Typography variant="subtitle">
                分類
              </Typography>
              <CategoryTree
                editMode
                data={categories}
                selectedId={selectedCategory?.id ?? null}
                onSelect={handleSelectCategory}
                onRename={handleRenameCategory}
                onAdd={handleAddCategory}
                onDelete={handleDeleteCategoryDialog}
                onMove={handleMoveCategoryDialog}
                onChangeCover={handleChangeCoverDialog}
              />
            </aside>

            {/* Right */}
            <main className="rounded-xl p-3 bg-gray-100 h-[calc(100vh-120px)] overflow-y-scroll flex flex-col gap-4">
              <div className="text-right">
                <ActionButton label="匯出數據報表" onClick={exportData} iconName="fa-file-export" variant="outline"/>
              </div>
              <div className="flex items-baseline justify-between">
                <Typography variant="subtitle">
                  {selectedCategory ? selectedCategory.name : "請從左側選擇分類"}
                </Typography>
                <Typography variant="captionMuted">
                  {selectedCategory ? `顯示文章：${filteredPosts.length} 篇` : ""}
                </Typography>
              </div>

              {selectedCategory ? (
                <PostList
                  categories={categories}
                  posts={filteredPosts}
                  onClickPost={openEdit}
                />
              ) : (
                <div className="text-gray-500 p-3">尚未選擇分類</div>
              )}
            </main>

            <Dialog
              isOpen={dialogContent !== ''}
              onBackdropClick={handleCloseDialog}
              size={dialogContent === CONTENT_MANAGEMENT_MODAL_CONTENT.POST ? 'lg' : 'sm'}
            >
              {ContentManagementModalContent({
                categories,
                selectedCategory,
                editingPost,
                createPost,
                onDeletePost: handleDeletePostDialog,
                onConfirmDeletePost,
                onSaveRenameCategory,
                onSaveChangeCoverImage,
                onAddCategory,
                onDeleteCategory,
                onMoveCategory,
                onCloseDialog: handleCloseDialog,
              })[dialogContent] || <></>}
            </Dialog>
          </div>
        </div>
      }
    />
  )
}
